import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { EulaDocument } from '@/components/legal/EulaDocument';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { useAuth } from '@/app/AuthProvider';
import {
  isCurrentVersionAccepted,
  useAcceptEula,
  useCurrentEulaAcceptance,
} from '@/features/eula/hooks';
import {
  CURRENT_EULA_EFFECTIVE_DATE_DISPLAY,
  CURRENT_EULA_VERSION,
} from '@/lib/eula';

/**
 * Click-through acceptance gate. Mounted at /legal/eula/accept and pushed by
 * RequireEulaAcceptance whenever the signed-in user's most recent acceptance
 * doesn't match CURRENT_EULA_VERSION.
 *
 * E-SIGN / UETA-aligned acceptance flow:
 *   1. The user authenticates first (identity)
 *   2. Sees the full unmodified EULA text on screen (disclosure)
 *   3. Confirms they have read and agree via an explicit checkbox (intent)
 *   4. Clicks the affirmatively-labeled "I Agree and Accept" button (consent)
 *   5. The eula-accept Edge Function records identity, version, IP, UA, and
 *      timestamp into the append-only public.eula_acceptances table (record)
 *
 * "Decline" signs the user out and returns them to /auth/sign-in. We do not
 * leave a half-authenticated session lying around with no acceptance — that
 * defeats the gate.
 */
export function EulaAcceptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const startedAt = useRef<number>(Date.now());

  const acceptanceQuery = useCurrentEulaAcceptance(user?.id);
  const acceptMutation = useAcceptEula();

  const alreadyAccepted = useMemo(
    () => isCurrentVersionAccepted(acceptanceQuery.data),
    [acceptanceQuery.data],
  );

  // The "from" location pushed by RequireEulaAcceptance; we send the user
  // back to their original destination after a successful accept.
  const returnTo =
    (location.state as { from?: { pathname: string } } | null)?.from
      ?.pathname ?? '/dashboard';

  // Reset the agreed checkbox if the user backs out and returns.
  useEffect(() => {
    setAgreed(false);
    startedAt.current = Date.now();
  }, [user?.id]);

  if (authLoading || acceptanceQuery.isLoading) return <LoadingScreen />;

  // Defensive: if the user lands here unauthenticated (e.g. session expired
  // mid-flow) push them to sign-in. RequireEulaAcceptance is supposed to
  // wrap this page so we typically never hit this branch.
  if (!user || !profile) {
    return (
      <Navigate
        to="/auth/sign-in"
        state={{ from: { pathname: '/legal/eula/accept' } }}
        replace
      />
    );
  }

  // If the user is already current on this version (e.g. they navigated here
  // manually), bounce them to wherever they came from.
  if (alreadyAccepted) {
    return <Navigate to={returnTo} replace />;
  }

  const handleAccept = async () => {
    if (!agreed) return;
    try {
      await acceptMutation.mutateAsync({
        metadata: {
          page_path: '/legal/eula/accept',
          dwell_ms: Date.now() - startedAt.current,
          locale:
            typeof navigator !== 'undefined' ? navigator.language : 'unknown',
          screen_width:
            typeof window !== 'undefined' ? window.innerWidth : null,
          screen_height:
            typeof window !== 'undefined' ? window.innerHeight : null,
          // user_agent is captured server-side from the request headers,
          // which is the only trustworthy source. We don't bother sending
          // navigator.userAgent here.
        },
      });
      toast.success('Acceptance recorded. Welcome to Atlas.');
      navigate(returnTo, { replace: true });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Could not record acceptance. Please try again.';
      toast.error(msg);
    }
  };

  const handleDecline = async () => {
    try {
      await signOut();
    } finally {
      // Whether sign-out succeeded or failed, get them off this page.
      navigate('/auth/sign-in', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="primary" tone="humus" className="h-7" />
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.6} />
            <span>
              Signed in as{' '}
              <span className="font-medium text-foreground">
                {profile.email}
              </span>
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 pt-8">
        <div className="rounded-lg border border-border bg-secondary/60 p-4 text-sm">
          <p className="font-medium text-foreground">
            One-time acceptance required
          </p>
          <p className="mt-1 text-muted-foreground">
            Atlas is internal-use software. Before we can let you in, please
            review the End-User License Agreement below and confirm your
            acceptance. Your username, IP address, device information, EULA
            version, and timestamp will be logged as evidence of your
            acceptance (see §11 and §21).
          </p>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-6 py-8 leading-body">
        <EulaDocument />
      </article>

      <div className="sticky bottom-0 border-t border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-6 py-5">
          <label
            htmlFor="eula-agree"
            className="flex cursor-pointer items-start gap-3 text-sm"
          >
            <input
              id="eula-agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={acceptMutation.isPending}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            />
            <span>
              I have read and agree to the Virro Atlas End-User License
              Agreement ({CURRENT_EULA_VERSION}, effective{' '}
              {CURRENT_EULA_EFFECTIVE_DATE_DISPLAY}). I understand my
              acceptance is binding under E-SIGN and UETA.
            </span>
          </label>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button
              variant="ghost"
              onClick={handleDecline}
              disabled={acceptMutation.isPending}
            >
              Decline and sign out
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!agreed || acceptMutation.isPending}
            >
              {acceptMutation.isPending
                ? 'Recording…'
                : 'I Agree and Accept'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
